import React, { useEffect, useMemo, useRef, useState } from 'react';
import StatusAlert from '../../Components/Common/StatusAlert';
import { Button } from '../../shadcn/components/ui/button';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../context/AuthContext';
import { useTranslation } from '../../context/Internationalization';
import { getStorage } from '../../auth/storage';
import { LOGIN_ROUTE } from '../../routes';
import AuthLayout from '../Login/AuthLayout';
import RegistrationForm from './RegistrationForm';
import { clearRegistrationProgress, isRecoveringRegistration, readRegistrationProgress } from './registrationProgress';
import { createRegistrationService, registrationClubErrors, registrationErrors, registrationPhotoError, validateLogo } from './registrationService';
import { DEFAULT_REGISTRATION_COUNTRY, DEFAULT_REGISTRATION_SPORT } from './registrationOptions';
import useRegistrationImagePreview from './useRegistrationImagePreview';

// The light blue brand colour (brandColorsOptions).
const DEFAULT_PRIMARY_COLOR = '#00CCCC';

const knownErrors = new Set([
  'registration_creation_uncertain', 'registration_club_unavailable', 'registration_access_changed',
  'registration_check_fields', 'registration_busy', 'session_identity_changed',
  'registration_billing_setup_failed',
]);
const logoErrors = new Set(['registration_reselect_logo', 'registration_logo_retry', 'registration_invalid_logo']);

function RegistrationForIdentity({ auth }) {
  const uid = auth.firebaseUser.uid;
  const user = auth.authState.user;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [progress, setProgress] = useState(() => readRegistrationProgress(uid));
  const recovering = isRecoveringRegistration(progress);
  // Journal updates must not switch the visible step during an active submission.
  const [submissionView, setSubmissionView] = useState(null);
  const visibleProgress = submissionView ? submissionView.progress : progress;
  const [step, setStep] = useState('club');
  const [values, setValues] = useState(() => {
    const saved = progress?.values || {};
    return {
      firstName: user?.firstName || '', lastName: user?.lastName || '', name: '',
      primaryColor: DEFAULT_PRIMARY_COLOR, ...saved,
      // Hidden defaults apply only before creation; recovery must match the original request.
      country: recovering ? saved.country : DEFAULT_REGISTRATION_COUNTRY,
      sport: recovering ? saved.sport : DEFAULT_REGISTRATION_SPORT, termsAccepted: false,
    };
  });
  const [logo, setLogo] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [savedPhoto, setSavedPhoto] = useState('');
  const logoPreview = useRegistrationImagePreview(logo);
  const photoPreview = useRegistrationImagePreview(registrationPhotoError(photo) ? null : photo);
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const pending = useRef(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const service = useMemo(() => createRegistrationService({
    uid, coreUserId: user?._id,
    onProgress: (next) => { if (mounted.current) setProgress(next); },
    onPhotoSaved: (url) => {
      if (mounted.current) { setSavedPhoto(url); setPhoto(null); }
    },
  }), [uid, user?._id]);

  const submit = async (event) => {
    event.preventDefault();
    if (pending.current) return;
    if (!recovering) {
      const clubErrors = registrationClubErrors(values, logo);
      const fieldErrors = step === 'club' ? clubErrors : registrationErrors(values, logo, photo);
      setErrors(fieldErrors);
      if (Object.keys(fieldErrors).length) {
        if (Object.keys(clubErrors).length) setStep('club');
        setError('');
        const form = event.currentTarget;
        requestAnimationFrame(() => form.querySelector('[aria-invalid="true"]')?.focus());
        return;
      }
      if (step === 'club') {
        setError('');
        setStep('admin');
        return;
      }
    }
    pending.current = true;
    setSubmissionView({ progress });
    let completed = false;
    setBusy(true);
    setError('');
    setErrors({});
    try {
      const result = await service.submit(values, logo, photo);
      if (!mounted.current) return;
      getStorage().setItem('activeClubId', String(result.progress.clubId));
      const session = await auth.refreshSession({ force: true, registerIdentity: false, forceTokenRefresh: false });
      if (getStorage().getItem('firebaseUid') !== uid) return;
      if (!session.user.clubs?.some((club) => club._id === result.progress.clubId)) {
        throw new Error('registration_club_unavailable');
      }
      clearRegistrationProgress(uid);
      navigate(session.destination, { replace: true });
      completed = true;
    } catch (failure) {
      if (failure.message === 'registration_access_changed') {
        // Another tab can finish setup while this form is still open.
        clearRegistrationProgress(uid);
        await auth.refreshSession({ force: true, registerIdentity: false }).catch(() => {});
      } else if (mounted.current && failure.field === 'photo') {
        setStep('admin');
        setErrors((current) => ({ ...current, photo: failure.message }));
      } else if (mounted.current && logoErrors.has(failure.message)) {
        setStep('club');
        setErrors((current) => ({ ...current, logo: failure.message }));
      } else if (mounted.current) {
        setError(knownErrors.has(failure.message) ? failure.message : 'registration_save_failed');
      }
    } finally {
      if (!completed) {
        pending.current = false;
        if (mounted.current) { setSubmissionView(null); setBusy(false); }
      }
    }
  };
  const removeLogo = async () => {
    if (pending.current) return;
    if (!progress?.clubId) { setLogo(null); setErrors((current) => ({ ...current, logo: null })); return; }
    pending.current = true;
    setBusy(true);
    setError('');
    try { await service.removeLogo(); setLogo(null); setErrors((current) => ({ ...current, logo: null })); }
    catch (_) { if (mounted.current) setErrors((current) => ({ ...current, logo: 'registration_logo_remove_failed' })); }
    finally { pending.current = false; if (mounted.current) setBusy(false); }
  };

  if (auth.session?.kind !== 'onboarding' && !progress) return <Navigate to={auth.session.destination} replace />;
  if (!auth.firebaseUser.emailVerified || !auth.firebaseUser.email || getStorage().getItem('impersonate')) {
    return <AuthLayout><div className="flex flex-col gap-6"><StatusAlert severity="info">{t('registration_verified_email_required')}</StatusAlert>
      <Button size="lg" onClick={auth.logout}>{t('registration_change_account')}</Button></div></AuthLayout>;
  }
  return <AuthLayout clubLogo={step === 'admin' && !isRecoveringRegistration(visibleProgress) ? logoPreview : undefined}>
    <RegistrationForm values={values} email={auth.firebaseUser.email}
    step={step} onBack={() => { setStep('club'); setErrors({}); setError(''); }}
    errors={errors} error={error} busy={busy} progress={visibleProgress} logoPreview={logoPreview}
    photoPreview={photoPreview || savedPhoto || user?.photo} photoSelected={Boolean(photo)}
    onPhoto={(file) => {
      setPhoto(file);
      setErrors((current) => ({ ...current, photo: registrationPhotoError(file) }));
      setError('');
    }}
    onChange={(name, value) => {
      setValues((current) => ({ ...current, [name]: value }));
      setErrors((current) => ({ ...current, [name]: null }));
    }}
    onLogo={(file) => {
      setLogo(file);
      setErrors((current) => ({ ...current, logo: validateLogo(file) ? null : 'registration_invalid_logo' }));
    }} onRemoveLogo={removeLogo} onSubmit={submit} onSignOut={auth.logout} /></AuthLayout>;
}

export default function Registration() {
  const auth = useAuthContext();
  if (!auth.firebaseUser) return <Navigate to={LOGIN_ROUTE} replace />;
  return <RegistrationForIdentity key={auth.firebaseUser.uid} auth={auth} />;
}
